import { Injectable, inject } from '@angular/core';
import { LocationAcl, LocationAclGroups, LocationAclMembers, SecurityService } from '@era/locations-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { LocationsRouterStoreSelectors } from '../router';
import {
  AccessControlListApiActions,
  LocationAclPermissionsUiActions,
  LocationPermissionsComponentActions,
  LocationPermissionsResolverActions,
} from './location-acl.actions';

@Injectable()
export class LocationAclEffects {
  private readonly securityService = inject(SecurityService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  locationAclLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationPermissionsResolverActions.locationAclRequested),
      mergeMap(({ resourceId }) =>
        safeMap(
          this.securityService.getLocationAcls({ resourceId }),
          (locationAcl: LocationAcl) => AccessControlListApiActions.locationAclLoaded({ locationAcl }),
          (error) => AccessControlListApiActions.locationAclLoadedError({ error })
        )
      )
    );
  });

  locationAclMemberPutEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationAclPermissionsUiActions.locationAclMemberPut),
      mergeMap(({ aclMember }) =>
        this.securityService
          .putLocationAclMembers({ resourceId: aclMember.resourceId, locationAclMembers: aclMember })
          .pipe(
            map((locationAclMembers: LocationAclMembers) =>
              AccessControlListApiActions.locationAclMemberPut({ aclMember: locationAclMembers })
            )
          )
      )
    );
  });

  locationAclGroupPutEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationAclPermissionsUiActions.locationAclGroupPut),
      mergeMap(({ aclGroup }) =>
        this.securityService
          .putLocationAclGroups({ resourceId: aclGroup.resourceId, locationAclGroups: aclGroup })
          .pipe(
            map((locationAclGroups: LocationAclGroups) =>
              AccessControlListApiActions.locationAclGroupPut({ aclGroup: locationAclGroups })
            )
          )
      )
    );
  });

  locationComponentAddGroupEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationPermissionsComponentActions.addGroup),
      withLatestFrom(this.store.select(LocationsRouterStoreSelectors.selectLocationId)),
      mergeMap(([{ group }, location]) =>
        this.securityService
          .putLocationAclGroups({
            resourceId: location ?? '',
            locationAclGroups: {
              resourceId: location ?? '',
              groupId: group.id,
              allow: new Set(),
              deny: new Set(),
            },
          })
          .pipe(
            map((locationAclGroups: LocationAclGroups) =>
              AccessControlListApiActions.locationAclGroupPut({ aclGroup: locationAclGroups })
            )
          )
      )
    );
  });

  locationAclGroupDeleteEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationAclPermissionsUiActions.locationAclGroupRemove),
      mergeMap(({ aclGroup }) =>
        this.securityService
          .deleteLocationAclGroups({ resourceId: aclGroup.resourceId, locationAclGroups: aclGroup })
          .pipe(map(() => AccessControlListApiActions.locationAclGroupRemove({ aclGroup })))
      )
    );
  });
}
